import { addDays, addHours, addMinutes, addMonths, addSeconds, addYears, compareAsc, compareDesc, differenceInDays, differenceInMilliseconds, differenceInMinutes, differenceInMonths, differenceInSeconds, endOfDay, endOfMonth, format, formatISO, getDate, getDay, getDayOfYear, getWeekOfMonth, isDate, isSameDay, isSameMonth, isValid, nextDay, parse, parseJSON, set, setDate, startOfDay, startOfMinute, startOfMonth, startOfWeek, subDays, subMonths, subYears, } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { ja } from "date-fns/locale";
import { isNullable } from "../../type-guards";
import { ListUtils } from "../list-utils";
const TimeZone = "Asia/Tokyo";
const WeekdayList = ["日", "月", "火", "水", "木", "金", "土"];
export class DateUtils {
    static isDate(v) {
        return isDate(v);
    }
    static utcToJSTTime(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return jstDate;
    }
    static JSTTimeToUtc(d) {
        const utcDate = zonedTimeToUtc(d, TimeZone);
        return utcDate;
    }
    static format(d, f, localeOption = false) {
        return format(d, f, localeOption ? { locale: ja } : undefined);
    }
    static formatTzDate(d, f) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, f);
    }
    static formatToDisplayDate(d) {
        return format(d, "MM/dd HH:mm");
    }
    // JST変換
    static formatToTzDisplayDate(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "MM/dd HH:mm");
    }
    static formatToHHmm(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "HH:mm");
    }
    static formatToTzHHmm(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "HH:mm");
    }
    static formatToMMddHHmm(d) {
        return format(d, "MM/dd HH:mm");
    }
    static formatToMMdd(d) {
        return format(d, "MM/dd");
    }
    // JST変換
    static formatToTzMMdd(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "MM/dd");
    }
    static formatToyyyyMMdd(d) {
        return format(d, "yyyy/MM/dd");
    }
    static formatToTzyyyyMMdd(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyy/MM/dd");
    }
    static formatToTzyyMMdd(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyyMMdd");
    }
    static formatToAlphaMd(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "M/d");
    }
    static formatToDate(d) {
        return formatISO(d, { representation: "date" });
    }
    static formatToTime(d) {
        return formatISO(d, { representation: "time" });
    }
    static formatToAlphaDateTime(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyy-MM-dd HH:mm");
    }
    static formatToTzSlashDateTime(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyy/MM/dd HH:mm");
    }
    static formatToTzSlashDate(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const day = WeekdayList[jstDate.getDay()];
        return format(jstDate, "yyyy/MM/dd") + `(${day})`;
    }
    static formatToAlphaDate(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyy-MM-dd");
    }
    static formatToTzDate(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyy-MM-dd");
    }
    static formatToDateTime(d) {
        // trim double quotation mark (")
        return JSON.stringify(d).slice(1, -1);
    }
    static formatToTzDateTime(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyy-MM-dd HH:mm:ss");
    }
    static formatToTzMonth(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyy-MM");
    }
    // 日本のタイムゾーンを考慮しない
    static formatDay(v) {
        return WeekdayList[v.getDay()];
    }
    // 日本のタイムゾーンを考慮する 2024-02-19 08:30:00.000 +0900
    static formatJstDay(v) {
        const jstDate = utcToZonedTime(v, TimeZone);
        return WeekdayList[jstDate.getDay()];
    }
    static formatToyyyyMMdd_HHmmss(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyyMMdd_HHmmss");
    }
    static formatToyyyyMMddHHmmssSSS(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyyyMMddHHmmssSSS");
    }
    static formatToBillingSerialDate(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return format(jstDate, "yyMM");
    }
    // 12月1日(火)形式に変換する
    static formatToMonthDay(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const day = WeekdayList[jstDate.getDay()];
        return `${format(jstDate, "M月d日")}(${day})`;
    }
    // 同じ日なら時間のみ、異なる場合は日付を表示
    static formatRelativeTime(d, base) {
        if (!isSameDay(d, base)) {
            return DateUtils.formatToMMddHHmm(d);
        }
        return DateUtils.formatToHHmm(d);
    }
    static fromFormat(v, format) {
        const result = parse(v, format, new Date());
        return DateUtils.isValidDate(result) ? result : undefined;
    }
    static fromTzFormat(v, format) {
        const d = parse(v, format, new Date());
        return zonedTimeToUtc(d, TimeZone);
    }
    static fromDateFormat(v) {
        return parse(v, "yyyy-MM-dd", new Date());
    }
    static fromTzDateFormat(v) {
        const d = parse(v, "yyyy-MM-dd", new Date());
        return zonedTimeToUtc(d, TimeZone);
    }
    static fromDateTimeFormat(v) {
        return parseJSON(v);
    }
    static startOfMinute(d) {
        return startOfMinute(d);
    }
    static startOfMinuteJST(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const startOfLocalDate = startOfMinute(jstDate);
        return zonedTimeToUtc(startOfLocalDate, TimeZone);
    }
    static startOfDay(d) {
        return startOfDay(d);
    }
    static startOfDayJST(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const startOfLocalDate = startOfDay(jstDate);
        return zonedTimeToUtc(startOfLocalDate, TimeZone);
    }
    static noonOfDay(d) {
        return addHours(startOfDay(d), 12);
    }
    static noonOfDayJST(d) {
        return addHours(DateUtils.startOfDayJST(d), 12);
    }
    static endOfDay(d) {
        return endOfDay(d);
    }
    static endOfDayJST(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const endOfLocalDate = endOfDay(jstDate);
        return zonedTimeToUtc(endOfLocalDate, TimeZone);
    }
    static startOfWeekJST(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const startOfLocalDate = startOfWeek(jstDate);
        return zonedTimeToUtc(startOfLocalDate, TimeZone);
    }
    static getMonthDates(d) {
        const start = DateUtils.startOfMonthJST(d);
        const end = DateUtils.endOfMonthJST(d);
        return ListUtils.newList(end.getDate()).map((_, i) => DateUtils.addDays(start, i));
    }
    static startOfMonth(d) {
        return startOfMonth(d);
    }
    static startOfMonthJST(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const startOfLocalDate = startOfMonth(jstDate);
        return zonedTimeToUtc(startOfLocalDate, TimeZone);
    }
    static endOfMonthJST(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const endOfLocalDate = endOfMonth(jstDate);
        return zonedTimeToUtc(endOfLocalDate, TimeZone);
    }
    static endOfMonth(d) {
        return endOfMonth(d);
    }
    static getDateJST(d) {
        const jstDate = utcToZonedTime(d, TimeZone);
        return getDate(jstDate);
    }
    static toSameDate(origin, target) {
        const jstOrigin = utcToZonedTime(origin, TimeZone);
        const jstTarget = utcToZonedTime(target, TimeZone);
        const result = set(jstOrigin, {
            year: jstTarget.getFullYear(),
            month: jstTarget.getMonth(),
            date: jstTarget.getDate(),
        });
        return zonedTimeToUtc(result, TimeZone);
    }
    static addSeconds(d, seconds) {
        return addSeconds(d, seconds);
    }
    static addMinutes(d, minutes) {
        return addMinutes(d, minutes);
    }
    static addHours(d, hours) {
        return addHours(d, hours);
    }
    static addDays(d, days) {
        return addDays(d, days);
    }
    static subDays(d, days) {
        return subDays(d, days);
    }
    static tomorrow(d) {
        return addDays(d, 1);
    }
    static addMonths(d, months) {
        return addMonths(d, months);
    }
    static addMonthsJST(d, months) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const addMonthLocalDate = addMonths(jstDate, months);
        return zonedTimeToUtc(addMonthLocalDate, TimeZone);
    }
    static subMonths(d, months) {
        return subMonths(d, months);
    }
    static subMonthsJST(d, months) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const subMonthLocalDate = subMonths(jstDate, months);
        return zonedTimeToUtc(subMonthLocalDate, TimeZone);
    }
    static addYears(d, years) {
        return addYears(d, years);
    }
    static addYearsJST(d, years) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const addYearLocalDate = addYears(jstDate, years);
        return zonedTimeToUtc(addYearLocalDate, TimeZone);
    }
    static subYears(d, years) {
        return subYears(d, years);
    }
    static subYearsJST(d, years) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const subYearsLocalDate = subMonths(jstDate, years);
        return zonedTimeToUtc(subYearsLocalDate, TimeZone);
    }
    static setDate(d, dayOfMonth) {
        return setDate(d, dayOfMonth);
    }
    static setDateJST(d, dayOfMonth) {
        const jstDate = utcToZonedTime(d, TimeZone);
        const setLocalDate = setDate(jstDate, dayOfMonth);
        return zonedTimeToUtc(setLocalDate, TimeZone);
    }
    static nextDateJST(d, dayOfMonth) {
        return DateUtils.getDateJST(d) <= dayOfMonth
            ? DateUtils.setDateJST(d, dayOfMonth)
            : DateUtils.addMonthsJST(DateUtils.setDateJST(d, dayOfMonth), 1);
    }
    static previousDateJST(d, dayOfMonth) {
        return DateUtils.getDateJST(d) > dayOfMonth
            ? DateUtils.setDateJST(d, dayOfMonth)
            : DateUtils.subMonthsJST(DateUtils.setDateJST(d, dayOfMonth), 1);
    }
    static getDay(d) {
        return getDay(d);
    }
    static nextDay(d, dayOfWeek) {
        return nextDay(d, dayOfWeek);
    }
    static previousDay(d, dayOfWeek) {
        return nextDay(subDays(d, 8), dayOfWeek);
    }
    static minOfDays(days) {
        return new Date(Math.min(...days.map((d) => d.getTime())));
    }
    static maxOfDays(days) {
        return new Date(Math.max(...days.map((d) => d.getTime())));
    }
    static differenceInMonths(left, right) {
        return differenceInMonths(left, right);
    }
    static differenceInDays(left, right) {
        return differenceInDays(left, right);
    }
    static differenceInMinutes(left, right) {
        return differenceInMinutes(left, right);
    }
    static differenceInSeconds(left, right) {
        return differenceInSeconds(left, right);
    }
    static differenceInMilliseconds(left, right) {
        return differenceInMilliseconds(left, right);
    }
    static isSameTime(a, b) {
        return a.getTime() === b.getTime();
    }
    static isSameMinuteInJST(date1, date2) {
        const jstDate1 = utcToZonedTime(date1, TimeZone);
        const jstDate2 = utcToZonedTime(date2, TimeZone);
        return (jstDate1.getFullYear() === jstDate2.getFullYear() &&
            jstDate1.getMonth() === jstDate2.getMonth() &&
            jstDate1.getDate() === jstDate2.getDate() &&
            jstDate1.getHours() === jstDate2.getHours() &&
            jstDate1.getMinutes() === jstDate2.getMinutes());
    }
    static isSameDay(a, b) {
        if (isNullable(a) && isNullable(b)) {
            return true;
        }
        if (isNullable(a) || isNullable(b)) {
            return false;
        }
        return isSameDay(a, b);
    }
    static isAmJST(v) {
        const date = utcToZonedTime(v, TimeZone);
        return date.getHours() < 12;
    }
    static isPmJST(v) {
        const date = utcToZonedTime(v, TimeZone);
        return date.getHours() >= 12;
    }
    static isSameMonth(a, b) {
        return isSameMonth(a, b);
    }
    static isSameMonthJST(a, b) {
        const aJST = utcToZonedTime(a, TimeZone);
        const bJST = utcToZonedTime(b, TimeZone);
        return isSameMonth(aJST, bJST);
    }
    static isWeekday(d) {
        return !(getDay(d) === 0 || getDay(d) === 6);
    }
    static dayOfYear(d) {
        return getDayOfYear(d);
    }
    static weekOfMonth(d) {
        return getWeekOfMonth(d);
    }
    static compare(a, b) {
        return compareAsc(a, b) > 0;
    }
    static compareAsc(a, b) {
        return compareAsc(a, b);
    }
    static compareDesc(a, b) {
        return compareDesc(a, b);
    }
    static isValidDate(d) {
        return isValid(d) && Object.prototype.toString.call(d) === "[object Date]";
    }
    static isValidPeriod(start, end) {
        if (isNullable(start) || isNullable(end)) {
            return false;
        }
        return start.getTime() < end.getTime();
    }
    static middleOfPeriod(v) {
        return new Date((v.start.getTime() + v.end.getTime()) / 2);
    }
    static isOverlapping(a, b) {
        return compareAsc(b.end, a.start) >= 0 && compareAsc(a.end, b.start) >= 0;
    }
    static isPartiallyOverlapping(a, b) {
        return !(compareAsc(b.start, a.end) >= 0 || compareAsc(a.start, b.end) >= 0);
    }
    static isWithin(period, d) {
        return (DateUtils.compareAsc(d, period.start) >= 0 &&
            DateUtils.compareAsc(period.end, d) >= 0);
    }
    static toPeriod(base) {
        return {
            start: DateUtils.startOfDay(base),
            end: DateUtils.endOfDay(base),
        };
    }
    static displayRelativeTime(minutes) {
        const days = Math.floor(minutes / (60 * 24));
        const remainingMinutes = minutes % (60 * 24);
        const hours = Math.floor(remainingMinutes / 60);
        const mins = remainingMinutes % 60;
        const timeStr = mins === 0 ? `${hours}時` : `${hours}時${mins}分`;
        if (days === 0) {
            return timeStr;
        }
        if (days < 3) {
            return ["当日", "翌日", "翌々日"][days] + timeStr;
        }
        return `${days}日後${timeStr}`;
    }
    static displayElapsedDays(dayCount) {
        if (dayCount > 0) {
            return dayCount < 3
                ? ["当日", "翌日", "翌々日"].at(dayCount)
                : `${dayCount}日後`;
        }
        return dayCount > -3
            ? ["当日", "前日", "前々日"].at(-dayCount)
            : `${-dayCount}日前`;
    }
    static displayElapsedMonths(dayCount) {
        if (dayCount > 0) {
            return dayCount < 3
                ? ["当月", "翌月", "翌々月"].at(dayCount)
                : `${dayCount}ヶ月後`;
        }
        return dayCount > -3
            ? ["当月", "前月", "前々月"].at(-dayCount)
            : `${-dayCount}ヶ月前`;
    }
    static min(dates) {
        return Array.from(dates).sort(DateUtils.compareAsc).at(0);
    }
    static max(dates) {
        return Array.from(dates).sort(DateUtils.compareDesc).at(0);
    }
}
//# sourceMappingURL=date-utils.js.map